import React from 'react';
import { Card, phone } from '@parsina/framework';
import { Clock } from 'lucide-react';
import { C, CT, statusColors } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { waitedMinutes, visitOverrun, WAIT_WARN_MIN, displayName } from '../../lib/appointments.js';
import { durationLabel } from '../../lib/durationLabel.js';
import StatusPill from '../StatusPill.jsx';
import PatientAvatar from '../PatientAvatar.jsx';

// One appointment as a mobile CARD.
//
// This is the phone twin of the desktop `AppointmentRow`, and deliberately a
// separate component (§1.1: "same row, two surfaces, no shared component"). The
// desktop row is one flex line with nine children and a hover-repainted
// background — a hover affordance has no touch equivalent (§6), and nine
// children do not fit 320px. This is two lines inside a tappable card.
//
// The WHOLE card is the tap target (§4: "whole card taps into the record"), via
// the kit `Card`'s onClick form — a real <button>, with `role="link"` because
// the tap navigates. Never a `<div onClick>`.
//
// The escalation vocabulary is the desktop's, verbatim, from the SAME
// derivations: waited → amber past WAIT_WARN_MIN; an open visit normal → over →
// critical. Two surfaces disagreeing about whether a visit is running late
// would be worse than either alone.

const OVERRUN_CHIP = {
  normal: { bg: statusColors.in_visit.bg, color: statusColors.in_visit.text },
  over: { bg: CT.warnSoft, color: CT.warnInk },
  critical: { bg: CT.dangerSoft, color: CT.dangerInk },
};

const Chip = ({ bg, color, children }) => (
  <span
    style={{
      fontSize: '0.65625rem',
      fontWeight: 700,
      borderRadius: '999px',
      padding: '0.125rem 0.5rem',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      background: bg,
      color,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.1875rem',
    }}
  >
    {children}
  </span>
);

const MobileAppointmentCard = ({ apt, queuePos, nowMs, onOpen, testId }) => {
  const { t, formatDigits } = useTranslation();

  const waited = apt.status === 'arrived' ? waitedMinutes(apt, nowMs) : null;
  const { elapsedMin, level } =
    apt.status === 'in_visit' ? visitOverrun(apt, nowMs) : { elapsedMin: null, level: 'normal' };
  const overChip = OVERRUN_CHIP[level];

  const name = displayName(apt) || t('today.row.unknownPatient');
  // A «ثبت سریع» row has no patient record to open — placeholderLabel carries
  // the name INSTEAD of patientId, and exactly one of the two is ever set. So
  // this card is not tappable: a link to nothing is worse than no link.
  const walkIn = !apt.patientId;

  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', minWidth: 0 }}>
        <div
          style={{
            fontSize: '0.8125rem',
            fontWeight: 800,
            color: C.textPrimary,
            flexShrink: 0,
            minWidth: '2.75rem',
          }}
          dir="ltr"
        >
          {apt.time ? formatDigits(apt.time) : t('common.labels.none')}
        </div>
        <PatientAvatar name={name} src={apt.patient?.avatarUrl} size={32} bg={C.navy} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            dir="auto"
            style={{
              fontSize: '0.8125rem',
              fontWeight: 700,
              color: C.textPrimary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </div>
          {apt.patient?.phone && (
            <div
              dir="ltr"
              style={{
                fontSize: '0.6875rem',
                color: C.textMuted,
                unicodeBidi: 'isolate',
                whiteSpace: 'nowrap',
              }}
            >
              {formatDigits(phone.forDisplay(apt.patient.phone))}
            </div>
          )}
        </div>
        <StatusPill status={apt.status} />
      </div>

      {(waited != null || elapsedMin != null || queuePos != null || walkIn) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.375rem',
            marginTop: '0.5rem',
          }}
        >
          {queuePos != null && (
            <Chip
              bg={queuePos === 1 ? statusColors.arrived.bg : CT.surface2}
              color={queuePos === 1 ? statusColors.arrived.text : C.textSecondary}
            >
              {queuePos === 1
                ? t('today.queue.positionNext')
                : t('today.queue.position', { position: queuePos })}
            </Chip>
          )}
          {waited != null && (
            <Chip
              bg={waited > WAIT_WARN_MIN ? CT.warnSoft : CT.surface2}
              color={waited > WAIT_WARN_MIN ? CT.warnInk : C.textSecondary}
            >
              <Clock size={11} strokeWidth={2.4} aria-hidden />
              {durationLabel(t, 'today.queue.waited', waited)}
            </Chip>
          )}
          {elapsedMin != null && (
            <Chip bg={overChip.bg} color={overChip.color}>
              <Clock size={11} strokeWidth={2.4} aria-hidden />
              {durationLabel(t, 'today.queue.elapsed', elapsedMin)}
            </Chip>
          )}
          {walkIn && (
            <Chip bg={CT.surface2} color={C.textSecondary}>
              {t('today.row.walkIn')}
            </Chip>
          )}
        </div>
      )}
    </>
  );

  const style = { padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '0.875rem' };

  if (walkIn) {
    return (
      <Card style={style} data-testid={testId}>
        {body}
      </Card>
    );
  }

  return (
    <Card
      onClick={() => onOpen?.(apt)}
      role="link"
      // The tap opens جلسهٔ درمان, so the accessible name has to say so — it
      // announced «باز کردن پرونده» while navigating to the visit workspace.
      aria-label={t('today.row.visitFor', { name })}
      style={style}
      data-testid={testId}
    >
      {body}
    </Card>
  );
};

export default MobileAppointmentCard;
